import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const ManageCategories = () => {
  const [categories, setCategories] = useState([])
  const [filteredCategories, setFilteredCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: ''
  })

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(API_ENDPOINTS.CATEGORIES)
      setCategories(response.data)
      setFilteredCategories(response.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to fetch categories. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    let filtered = [...categories]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(category =>
        category.name?.toLowerCase().includes(searchLower)
      )
    }

    setFilteredCategories(filtered)
  }, [categories, filters])

  const {
    paginatedItems: paginatedCategories,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex
  } = usePagination(filteredCategories)

  const handleEdit = async (category) => {
    // Escape HTML to prevent XSS
    const escapedName = category.name.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    
    const { value: formValues } = await Swal.fire({
      title: 'Edit Category',
      html: `
        <input id="swal-input1" class="swal2-input" placeholder="Category Name" value="${escapedName}" required>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      preConfirm: () => {
        const name = document.getElementById('swal-input1').value.trim()
        if (!name) {
          Swal.showValidationMessage('Category name is required')
          return false
        }
        return { name }
      },
      didOpen: () => {
        const input = document.getElementById('swal-input1')
        input.focus()
        input.select()
      }
    })

    if (formValues) {
      try {
        const payload = {
          name: formValues.name
        }

        await apiClient.put(`${API_ENDPOINTS.CATEGORIES}${category.id}/`, payload)

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Category updated successfully!'
        })

        fetchCategories() // Refresh the list
      } catch (error) {
        console.error('Error updating category:', error)
        const errorMessage = error.response?.data?.name?.[0] || 
                            error.response?.data?.non_field_errors?.[0] ||
                            error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to update category. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  const handleDelete = async (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId)
    
    // Escape HTML to prevent XSS
    const escapedName = category?.name?.replace(/"/g, '&quot;').replace(/'/g, '&#39;') || ''
    
    const { value: typedName } = await Swal.fire({
      title: 'Delete Category',
      html: `
        <p>Are you sure you want to delete this category?</p>
        <p class="text-danger"><strong>This action cannot be undone!</strong></p>
        <p>Type <strong>"${escapedName}"</strong> to confirm:</p>
        <input id="swal-input" class="swal2-input" placeholder="Type category name">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: () => {
        const input = document.getElementById('swal-input')
        const confirmButton = Swal.getConfirmButton()
        
        // Initially disable the confirm button
        confirmButton.disabled = true
        confirmButton.style.opacity = '0.5'
        confirmButton.style.cursor = 'not-allowed'
        
        // Add input event listener to enable/disable button based on match
        input.addEventListener('input', () => {
          const inputValue = input.value.trim()
          const matches = inputValue === category?.name
          
          if (matches) {
            confirmButton.disabled = false
            confirmButton.style.opacity = '1'
            confirmButton.style.cursor = 'pointer'
          } else {
            confirmButton.disabled = true
            confirmButton.style.opacity = '0.5'
            confirmButton.style.cursor = 'not-allowed'
          }
        })
        
        input.focus()
      },
      preConfirm: () => {
        const input = document.getElementById('swal-input')
        const inputValue = input.value.trim()
        
        if (!inputValue) {
          Swal.showValidationMessage('You need to type the category name!')
          return false
        }
        
        if (inputValue !== category?.name) {
          Swal.showValidationMessage('The name does not match!')
          return false
        }
        
        return inputValue
      }
    })

    if (typedName === category?.name) {
      try {
        await apiClient.delete(`${API_ENDPOINTS.CATEGORIES}${categoryId}/`)
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Category has been deleted.',
          timer: 2000,
          showConfirmButton: false
        })
        fetchCategories() // Refresh the list
      } catch (error) {
        console.error('Error deleting category:', error)
        const errorMessage = error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to delete category. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Categories</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Categories</li>
            <li className="breadcrumb-item active">Manage Categories</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-tags me-2"></i>
                  Manage Categories
                </h5>

                {/* Filters */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search categories..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                </div>

                {/* Categories Table */}
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th className="text-end" style={{ width: '150px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCategories.length === 0 ? (
                          <tr>
                            <td colSpan="2" className="text-center">No categories found</td>
                          </tr>
                        ) : (
                          paginatedCategories.map(category => (
                            <tr key={category.id}>
                              
                              <td className="align-middle">
                                <strong>{category.name}</strong>
                              </td>
                              <td className="align-middle text-end">
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleEdit(category)}
                                    title="Edit category"
                                  >
                                    <i className="bi bi-pencil-fill"></i>
                                    <span className="ms-1 d-none d-md-inline">Edit</span>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(category.id)}
                                    title="Delete category"
                                  >
                                    <i className="bi bi-trash-fill"></i>
                                    <span className="ms-1 d-none d-md-inline">Delete</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      pageSize={pageSize}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      itemLabel="categories"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageCategories

